import { NestedItemBasicInfo, NestedProjectExplorer } from '@tableau/nested-project-explorer';
import { DropdownButton, DropdownButtonType } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';

import { selectSpaceInfo } from '../../../app/selectors/SpaceInfoSelectors';
import { AppState } from '../../../app/state/AppState';
import { isProjectSpace } from '../../../app/state/SpaceInfo';
import { MapStateToProps } from '../../../app/utils/ConnectUtils';
import { Messages } from '../../../codegen/Localize';
import { UploadWorkbookStyles } from '../../styles/UploadWorkbookStyles';
import ProjectExplorerEffects, { useProjects } from '../../utils/ProjectExplorerEffects';

export interface ProjectExplorerDropdownProps {
  testId: string;
  initialProjectId: string;
  handleProjectSelect: (project: NestedItemBasicInfo) => void;
  dropdownLabel?: string;
}

export function InternalProjectExplorerDropdown(props: ProjectExplorerDropdownProps) {

  const projectTree = useProjects();
  const [selectedProject, setSelectedProject] = React.useState<NestedItemBasicInfo>();
  // Note: We have to use a wrapper object around done() because we don't want this functional component to
  // re-render every time the dropdown pop-up opens as it gives a new done function
  const [closeDropdown] = React.useState<{done: () => void}>({done: null});

  React.useEffect(
    function setupInitialProject() {
      const initialProject = ProjectExplorerEffects.getInitialProject(projectTree, props.initialProjectId);
      setSelectedProject(initialProject);
      props.handleProjectSelect(initialProject);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectTree, props.initialProjectId]
  );

  const handleChange = React.useCallback(
    (project: NestedItemBasicInfo) => {
      setSelectedProject(project);
      props.handleProjectSelect(project);
      if (project) {
        closeDropdown.done();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeDropdown]
  );

  const assignDoneFunction = (done: () => void) => { closeDropdown.done = done; };

  return (
    <DropdownButton
      name="projectId"
      testId={props.testId}
      label={props.dropdownLabel ?? Messages.project()}
      className={`${UploadWorkbookStyles.textField} ${UploadWorkbookStyles.projectDropdown}`}
      children={selectedProject?.name}
      onOpen={assignDoneFunction}
      popupContent={
        <div role="tree" id={'uploadWorkbookProjectExplorerId'} className={UploadWorkbookStyles.projectExplorerPopup}>
          {projectTree &&
            <NestedProjectExplorer
              topLevelDestinationStatus={'structurally_invalid'}
              projects={projectTree}
              onSelectedProjectChanged={handleChange}
              initiallySelectedProjectId={selectedProject?.id}
              selectRowOnExpansion={false}
              keepParentsOfSelectedItemExpanded={false}
            />
          }
        </div>
      }
      type={DropdownButtonType.Line}
      aria-haspopup={'tree'}
      aria-controls={'uploadWorkbookProjectExplorerId'}
    />
  );
}

const mapStateToProps: MapStateToProps<ProjectExplorerDropdownProps, ProjectExplorerDropdownProps> = (state: AppState, ownProps) =>{
  const spaceInfo = selectSpaceInfo(state);
  const projectId = isProjectSpace(spaceInfo) ? spaceInfo.item.id : null;
  return {
    ...ownProps,
    initialProjectId: ownProps.initialProjectId ?? projectId
  };
};

export const ProjectExplorerDropdown = connect(mapStateToProps)(React.memo(InternalProjectExplorerDropdown, (prevProps: any, nextProps: any) => prevProps !== nextProps));
